import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import { SHOW_MESSAGE, clearSelectedJob } from '~/actions';
import { ICounterOfferEntity, useConfirmJobMutation } from '~/domain/Jobs';
import { useAppSelector, useToggle } from '~/hooks';
import { routes } from '~/routes';

interface IUseConfirmInterview {
  professionalId: string;
  officeId?: string;
}

export const useConfirmInterview = ({ officeId }: IUseConfirmInterview) => {
  const dispatch = useDispatch();

  const selectedInterviewIdRef = useRef<string | null>(null);
  const counterOfferRef = useRef<ICounterOfferEntity | null>(null);
  const [isOpen, controls] = useToggle();

  const professional = useAppSelector((state) => state.user.professionalProfile);
  const confirmJobMutation = useConfirmJobMutation({
    // Interview confirms only ever surface onsite/no-followup messages here. The
    // `show_*_popup` outcomes are intentionally ignored: their popups render only
    // inside ConfirmCandidate, which is not mounted on this screen, so dispatching
    // their Redux flags from here would set state nothing can render.
    onOutcome: ({ outcome }, variables) => {
      if (outcome.type === 'onsite_confirmed') {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Interview Confirmed!',
            message:
              `Good news, ${variables.candidate?.full_name} has been confirmed ` +
              `for your onsite interview on ${outcome.localDate}.`,
            route: outcome.postingId
              ? generatePath(routes.jobOverview, { postingId: outcome.postingId })
              : null,
            isSuccess: true,
          },
        });
        return;
      }

      if (outcome.type === 'confirmed_no_followup') {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Good News!',
            message: 'Your shift has successfully been filled.',
            route: outcome.route,
            isSuccess: true,
          },
        });
      }
    },
  });

  const handleConfirmInterview = (interviewId: string, counterOffer: ICounterOfferEntity) => {
    selectedInterviewIdRef.current = interviewId;
    counterOfferRef.current = counterOffer;
    controls.setOn();
  };

  const handleConfirmCandidateInterview = (notes?: string) => {
    if (!selectedInterviewIdRef.current || !counterOfferRef.current || !professional) return;

    confirmJobMutation.mutate({
      jobId: selectedInterviewIdRef.current,
      data: { notes },
      counterOfferId: counterOfferRef.current.id,
      candidate: {
        id: professional.id,
        full_name: professional.full_name,
        profile_image: professional.profile_image,
      },
      officeId,
    });
  };

  const handleCloseConfirmInterview = () => {
    selectedInterviewIdRef.current = null;
    counterOfferRef.current = null;
    controls.setOff();
    confirmJobMutation.reset();
    dispatch(clearSelectedJob());
  };

  return {
    isConfirming: confirmJobMutation.isPending,
    isOpen,
    selectedInterviewId: selectedInterviewIdRef.current,
    handleCloseConfirmInterview,
    handleConfirmInterview,
    handleConfirmCandidateInterview,
  };
};
